import React, { useEffect } from 'react';
import { Panel } from '@ftdr/blueprint-components-react';
import SendDocuments, { Recipient } from '@components/SendDocuments';
import { getCurrentThemeConfig } from '@app/core/theme-config';
import { useNewOrderSendDocuments } from '@helpers/UseNewOrderSendDocuments';
import { CreateOrderBuyer, CreateOrderSeller } from '@apis/models';
import { NewOrderData } from '@helpers/new-order-loader';
import ProfileModel from '@app/models/profile.model';
import { isEmpty } from 'lodash';

export interface SendDocumentsSectionProps {
  values: NewOrderData;
  userDetails: ProfileModel;
  getBuyer: (req: NewOrderData) => Partial<CreateOrderBuyer>;
  getSeller: (req: NewOrderData) => Partial<CreateOrderSeller>;
  customCooperatingAgentDetails: {
    hasValue?: boolean;
    agentFirstName?: string;
    agentLastName?: string;
    agentEmail?: string;
  };
  projectedClosingDate: string;
  isSendDocumentsVisible: boolean;
  onValidStateChange: (isValid: boolean) => void;
  onRecipientsUpdate: (hasRecipients: boolean) => void;
  onRecipientsChange: (recipients: Recipient[]) => void;
}

const SendDocumentsSection: React.FC<SendDocumentsSectionProps> = ({
  values,
  userDetails,
  getBuyer,
  getSeller,
  customCooperatingAgentDetails,
  projectedClosingDate,
  isSendDocumentsVisible,
  onValidStateChange,
  onRecipientsUpdate,
  onRecipientsChange,
}) => {
  const { recipients, setRecipients, allowedTypes, getLatestRecipients } = useNewOrderSendDocuments(
    { values, userDetails },
    getBuyer,
    getSeller,
    customCooperatingAgentDetails,
    isSendDocumentsVisible,
  );

  const isRecipientSelected = (): boolean => {
    return recipients.some((x) => x.selectedTypes.length > 0 && !isEmpty(x.email));
  };

  useEffect(() => {
    onRecipientsUpdate(isRecipientSelected());
  }, [recipients, onRecipientsUpdate]);

  useEffect(() => {
    onRecipientsChange(recipients);
  }, [recipients, onRecipientsChange]);

  if (!isSendDocumentsVisible) {
    return null;
  }

  return (
    <div className="mt-4">
      <Panel
        padding="xl"
        rounded={getCurrentThemeConfig().newOrderSendDocumentsRounded}
        backgroundColor="white"
        borderT={getCurrentThemeConfig().newOrderSendDocumentsBorderT}
        borderColor="gray-100"
        shadow={getCurrentThemeConfig().newOrderSendDocumentsShadow}
      >
        <SendDocuments
          recipients={getLatestRecipients()}
          allowedTypes={allowedTypes}
          onChange={setRecipients}
          onUpdateFormValidState={onValidStateChange}
          newOrderClosingDate={projectedClosingDate}
          isNewOrder={true}
        />
      </Panel>
    </div>
  );
};

export default SendDocumentsSection;
